import * as React from "react";
import { Dialog as DialogPrimitive, DialogRootChangeEventDetails } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { XIcon } from "lucide-react";

const dialogSizeVariants = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  "2xl": "sm:max-w-5xl",
  "3xl": "sm:max-w-7xl",
  full: "max-w-full w-full max-h-full h-full overflow-auto",
};

export type DialogSize = keyof typeof dialogSizeVariants;

function Dialog({
  children,
  trigger,
  className,
  showCloseButton = false,
  size = "md",
  disableEscapeDismissal = false,
  onOpenChange,
  autoFocus = false,
  ...props
}: DialogPrimitive.Root.Props & {
  children: React.ReactNode;
  trigger?: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  size?: DialogSize;
  disableEscapeDismissal?: boolean;
  autoFocus?: boolean;
}) {
  const handleOpenChange = (open: boolean, details: DialogRootChangeEventDetails) => {
    if (disableEscapeDismissal && details.reason === "escape-key") {
      return;
    }
    onOpenChange?.(open, details);
  };
  return (
    <DialogPrimitive.Root data-slot="dialog" {...props} onOpenChange={handleOpenChange}>
      {trigger &&
        (React.isValidElement(trigger) ? <DialogTrigger render={trigger} /> : <DialogTrigger>{trigger}</DialogTrigger>)}
      <DialogPortal>
        <DialogOverlay />
        <DialogPopup
          size={size}
          showCloseButton={showCloseButton}
          autoFocus={autoFocus}
          className={className}
          children={children}
        />
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

function DialogTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(props: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      forceRender
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/30 duration-100 fixed inset-0 isolate z-1000",
        className
      )}
      {...props}
    />
  );
}

function DialogPopup({
  className,
  children,
  showCloseButton = false,
  size = "md",
  autoFocus = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  size?: keyof typeof dialogSizeVariants;
  autoFocus?: boolean;
}) {
  return (
    <DialogPrimitive.Popup
      data-slot="dialog-popup"
      initialFocus={autoFocus}
      className="group/dialog fixed inset-0 z-1000 flex items-center justify-center p-4 outline-none pointer-events-none"
      {...props}
    >
      <div
        className={cn(
          "pointer-events-auto bg-background group-data-open/dialog:animate-in group-data-closed/dialog:animate-out group-data-closed/dialog:fade-out-0 group-data-open/dialog:fade-in-0 group-data-closed/dialog:zoom-out-95 group-data-open/dialog:zoom-in-95 ring-foreground/10 grid max-w-[calc(100%-2rem)] rounded-xl text-sm ring-1 duration-100 relative w-full outline-none",
          dialogSizeVariants[size],
          className
        )}
      >
        {children}
        {showCloseButton && (
          <DialogClose
            render={
              <Button variant="ghost" className="absolute top-2 right-2" size="icon-sm">
                <XIcon />
                <span className="sr-only">Close</span>
              </Button>
            }
          />
        )}
      </div>
    </DialogPrimitive.Popup>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("gap-2 flex flex-col border-b border-b-border p-4", className)}
      {...props}
    />
  );
}

const DialogContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(function DialogContent(
  { className, ...props },
  ref
) {
  return <div ref={ref} data-slot="dialog-content" className={cn("p-4", className)} {...props} />;
});

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "bg-gray-50 rounded-b-xl border-t border-t-border p-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant="outline">Close</Button>} />}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-foreground text-lg font-semibold m-0 text-left", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3",
        className
      )}
      {...props}
    />
  );
}

Dialog.displayName = "Dialog";
DialogClose.displayName = "DialogClose";
DialogPopup.displayName = "DialogPopup";
DialogDescription.displayName = "DialogDescription";
DialogFooter.displayName = "DialogFooter";
DialogContent.displayName = "DialogContent";
DialogHeader.displayName = "DialogHeader";
DialogOverlay.displayName = "DialogOverlay";
DialogPortal.displayName = "DialogPortal";
DialogTitle.displayName = "DialogTitle";
DialogTrigger.displayName = "DialogTrigger";

const DialogNamespace = Object.assign(Dialog, {
  Close: DialogClose,
  Popup: DialogPopup,
  Description: DialogDescription,
  Footer: DialogFooter,
  Content: DialogContent,
  Header: DialogHeader,
  Overlay: DialogOverlay,
  Portal: DialogPortal,
  Title: DialogTitle,
  Trigger: DialogTrigger,
});

export { DialogNamespace as Dialog };
